import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, FileText, Sparkles } from 'lucide-react';
import { Document, Packer, Paragraph, Table, TableCell, TableRow, AlignmentType, WidthType, BorderStyle, HeadingLevel, VerticalAlign, HeightRule } from 'docx';
import { saveAs } from 'file-saver';

const MinistryReportModal = ({ isOpen, onClose, stats, theme, deptName, aiResult }) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const [reportPlans, setReportPlans] = useState("");
    const [reportNotes, setReportNotes] = useState("");

    // [AI Auto-Fill Logic]
    const handleAiAutoFill = () => {
        if (!aiResult) return;

        // Simple heuristic to split AI briefing into sections
        // Look for common keywords or bullet points
        const lines = aiResult.split('\n');
        let plans = [];
        let notes = [];

        let target = 'plans';
        lines.forEach(line => {
            const cleanLine = line.trim().replace(/^[-*•\d.]+\s*/, '');
            if (!cleanLine) return;

            if (line.includes('참고') || line.includes('주의') || line.includes('비고') || line.includes('기도')) target = 'notes';
            else if (line.includes('계획') || line.includes('실행')) target = 'plans';

            if (target === 'plans') plans.push(cleanLine);
            else notes.push(cleanLine);
        });

        if (plans.length > 0) setReportPlans(plans.slice(0, 5).map(p => `- ${p}`).join('\n'));
        if (notes.length > 0) setReportNotes(notes.slice(0, 3).map(p => `- ${p}`).join('\n'));
    };

    if (!isOpen) return null;

    // [Word Document Generator]
    const handleDownloadWord = async () => {
        setIsDownloading(true);

        try {
            // 1. Define Styles
            const commonFont = "Malgun Gothic";
            const lightBorder = { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" };
            const noBorder = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" };
            const tableHeaderFill = "F9FAFB";

            // 2. Metrics Table Cells - STRICT SYNTAX (No spread, no complex templates in constructors)

            // Data Prep
            const txtStudentCount = stats.studentCount + " 명";
            const txtStudentDelta = "전주 대비 " + (stats.studentDelta >= 0 ? '+' : '') + stats.studentDelta + " 명";
            const colorStudentDelta = stats.studentDelta >= 0 ? "059669" : "E11D48";

            const txtAttendanceRate = stats.attendanceRate + "%";
            const txtAttendanceDelta = "전주 대비 " + (stats.attendanceDelta > 0 ? '+' : '') + stats.attendanceDelta + "%";
            const colorAttendanceDelta = stats.attendanceDelta > 0 ? "059669" : "E11D48";

            const txtVisitationCount = stats.visitationCount + " 명";
            const txtVisitationRate = "목표 달성률 " + stats.visitationRate + "%";

            const txtFinanceBalance = stats.financeBalance.toLocaleString() + " 원";
            const txtFinanceDelta = "전월 대비 " + (stats.financeDelta > 0 ? '+' : '') + Math.round(stats.financeDelta / 10000) + " 만";
            const colorFinanceDelta = stats.financeDelta >= 0 ? "059669" : "E11D48";

            // Row 1
            const cellTotalStudents = new TableCell({
                children: [
                    new Paragraph({ text: "총 등록 인원", color: "555555", size: 20, font: commonFont }),
                    new Paragraph({ text: txtStudentCount, bold: true, size: 32, font: commonFont }),
                    new Paragraph({ text: txtStudentDelta, size: 16, font: commonFont, color: colorStudentDelta })
                ],
                padding: { top: 150, bottom: 150, left: 150, right: 150 },
            });

            const cellAttendance = new TableCell({
                children: [
                    new Paragraph({ text: "금주 출석률", color: "555555", size: 20, font: commonFont }),
                    new Paragraph({ text: txtAttendanceRate, bold: true, size: 32, font: commonFont }),
                    new Paragraph({ text: txtAttendanceDelta, size: 16, font: commonFont, color: colorAttendanceDelta })
                ],
                padding: { top: 150, bottom: 150, left: 150, right: 150 },
            });

            // Row 2
            const cellVisitation = new TableCell({
                children: [
                    new Paragraph({ text: "이번 달 심방", color: "555555", size: 20, font: commonFont }),
                    new Paragraph({ text: txtVisitationCount, bold: true, size: 32, font: commonFont }),
                    new Paragraph({ text: txtVisitationRate, size: 16, font: commonFont, color: "6B7280" })
                ],
                padding: { top: 150, bottom: 150, left: 150, right: 150 },
            });

            const cellFinance = new TableCell({
                children: [
                    new Paragraph({ text: "재정 잔액", color: "555555", size: 20, font: commonFont }),
                    new Paragraph({ text: txtFinanceBalance, bold: true, size: 32, font: commonFont }),
                    new Paragraph({ text: txtFinanceDelta, size: 16, font: commonFont, color: colorFinanceDelta })
                ],
                padding: { top: 150, bottom: 150, left: 150, right: 150 },
            });

            const metricsTable = new Table({
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: { top: lightBorder, bottom: lightBorder, left: lightBorder, right: lightBorder, insideHorizontal: lightBorder, insideVertical: lightBorder },
                rows: [
                    new TableRow({ children: [cellTotalStudents, cellAttendance] }),
                    new TableRow({ children: [cellVisitation, cellFinance] }),
                ],
            });

            // 3. Absentees Table Logic
            const headerTexts = ["이름", "연락처", "상태", "조치 및 면담 내용"];
            const headerCells = headerTexts.map((text, idx) => {
                return new TableCell({
                    children: [new Paragraph({ text: text, alignment: AlignmentType.CENTER, bold: true, size: 22, font: commonFont, color: "374151" })],
                    shading: { fill: tableHeaderFill },
                    verticalAlign: VerticalAlign.CENTER,
                    width: { size: idx === 3 ? 40 : 20, type: WidthType.PERCENTAGE }
                });
            });

            const absenteeHeaderRow = new TableRow({ children: headerCells });

            const absenteeDataRows = [];
            if (stats.absentees.length > 0) {
                stats.absentees.forEach(s => {
                    absenteeDataRows.push(new TableRow({
                        children: [
                            new TableCell({ children: [new Paragraph({ text: s.name, font: commonFont })] }),
                            new TableCell({ children: [new Paragraph({ text: s.phone || '-', font: commonFont, size: 18 })] }),
                            new TableCell({ children: [new Paragraph({ text: "4주 결석", color: "E11D48", bold: true, font: commonFont, size: 18 })] }),
                            new TableCell({ children: [new Paragraph({ text: "", font: commonFont })] }),
                        ]
                    }));
                });
            } else {
                absenteeDataRows.push(new TableRow({
                    children: [new TableCell({ columnSpan: 4, children: [new Paragraph({ text: "장기 결석자가 없습니다.", alignment: AlignmentType.CENTER, font: commonFont, color: "9CA3AF" })], padding: { top: 200, bottom: 200 } })]
                }));
            }

            const absenteesTable = new Table({
                width: { size: 100, type: WidthType.PERCENTAGE },
                rows: [absenteeHeaderRow, ...absenteeDataRows],
                borders: { top: lightBorder, bottom: lightBorder, left: lightBorder, right: lightBorder, insideHorizontal: lightBorder, insideVertical: lightBorder }
            });

            // 4. Plans Table (USER CONTENT)
            const planRows = [];
            const planLines = reportPlans.split('\n').filter(l => l.trim());
            const displayPlans = planLines.length > 0 ? planLines : ["", "", "", ""];

            displayPlans.forEach(line => {
                planRows.push(new TableRow({
                    children: [new TableCell({ children: [new Paragraph({ text: line, font: commonFont })], borders: { bottom: lightBorder, left: noBorder, right: noBorder, top: noBorder } })],
                    height: { value: 600, rule: HeightRule.AT_LEAST }
                }));
            });
            const plansTable = new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: planRows });

            // 5. Notes Table (USER CONTENT)
            const noteRows = [];
            const noteLines = reportNotes.split('\n').filter(l => l.trim());
            const displayNotes = noteLines.length > 0 ? noteLines : ["", "", ""];

            displayNotes.forEach((line) => {
                noteRows.push(new TableRow({
                    children: [
                        new TableCell({ children: [new Paragraph({ text: "• " + line, font: commonFont })], width: { size: 100, type: WidthType.PERCENTAGE }, borders: { bottom: lightBorder, left: noBorder, right: noBorder, top: noBorder } })
                    ],
                    height: { value: 600, rule: HeightRule.AT_LEAST }
                }));
            });
            const notesTable = new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: noteRows });


            // 6. Document Construction
            const titleText = deptName + " 주간 사역 보고서";
            const dateText = new Date().toLocaleDateString() + " (주일) | 부서: " + deptName + " | 작성: ____________ (인)";

            const doc = new Document({
                sections: [{
                    properties: {},
                    children: [
                        // Header
                        new Paragraph({
                            text: titleText,
                            heading: HeadingLevel.HEADING_1,
                            alignment: AlignmentType.CENTER,
                            spacing: { after: 100 },
                            run: { font: commonFont, bold: true, color: "111827" }
                        }),
                        new Paragraph({
                            text: dateText,
                            alignment: AlignmentType.CENTER,
                            spacing: { after: 400 },
                            run: { font: commonFont, size: 20, color: "6B7280" }
                        }),

                        // 1. Metrics
                        new Paragraph({ text: "1. 주요 현황", heading: HeadingLevel.HEADING_2, spacing: { before: 200, after: 150 }, run: { font: commonFont, bold: true } }),
                        metricsTable,

                        // 2. Absentees
                        new Paragraph({ text: "2. 장기 결석자 관리 및 면담", heading: HeadingLevel.HEADING_2, spacing: { before: 400, after: 150 }, run: { font: commonFont, bold: true } }),
                        absenteesTable,

                        // 3. Plans
                        new Paragraph({ text: "3. 금주 사역 결과 및 내주 계획", heading: HeadingLevel.HEADING_2, spacing: { before: 400, after: 150 }, run: { font: commonFont, bold: true } }),
                        plansTable,

                        // 4. Notes
                        new Paragraph({ text: "4. 비고 및 메모", heading: HeadingLevel.HEADING_2, spacing: { before: 400, after: 150 }, run: { font: commonFont, bold: true } }),
                        notesTable,

                        // Footer
                        new Paragraph({
                            text: "TheSTAFF Ministry Management System",
                            alignment: AlignmentType.CENTER,
                            spacing: { before: 600 },
                            run: { size: 16, color: "9CA3AF", font: commonFont }
                        })
                    ],
                }],
            });

            // Generate
            const blob = await Packer.toBlob(doc);
            saveAs(blob, deptName + "_Ministry_Report_" + new Date().toISOString().slice(0, 10) + ".docx");

        } catch (err) {
            console.error("Docx Fail:", err);
            alert("문서 생성 중 오류가 발생했습니다.");
        } finally {
            setIsDownloading(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden relative">
                {/* Header */}
                <div className="p-4 border-b flex justify-between items-center bg-white">
                    <h3 className="font-bold text-lg text-gray-800 flex items-center gap-2">
                        <FileText size={20} className="text-blue-600" /> 사역 보고서 미리보기
                    </h3>
                    <div className="flex gap-2">
                        {aiResult && (
                            <button
                                onClick={handleAiAutoFill}
                                className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg font-bold hover:bg-emerald-100 transition text-sm shadow-sm"
                            >
                                <Sparkles size={16} /> AI 자동 완성
                            </button>
                        )}
                        <button
                            onClick={handleDownloadWord}
                            disabled={isDownloading}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition disabled:opacity-50 text-sm shadow-sm"
                        >
                            {isDownloading ? '생성 중...' : <><Download size={16} /> Word 다운로드</>}
                        </button>
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition text-gray-400">
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Preview Area */}
                <div className="overflow-y-auto custom-scrollbar bg-gray-100 p-8 flex justify-center">
                    <div className="bg-white w-full max-w-[210mm] min-h-[297mm] p-12 shadow-md flex flex-col gap-6 text-slate-800 border border-gray-200">

                        {/* Title */}
                        <div className="text-center border-b-2 border-slate-900 pb-6">
                            <h1 className="text-3xl font-extrabold mb-3 text-slate-900">{deptName} 주간 사역 보고서</h1>
                            <p className="text-slate-500 text-sm font-medium">
                                {new Date().toLocaleDateString()} (주일) | 부서: {deptName} | 작성: ____________ (인)
                            </p>
                        </div>

                        {/* 1. Metrics */}
                        <div className="space-y-3">
                            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">1. 주요 현황</h2>
                            <div className="grid grid-cols-2 border border-slate-200 divide-x divide-slate-200">
                                <div className="divide-y divide-slate-200">
                                    <div className="p-4 flex flex-col">
                                        <span className="text-slate-500 text-xs font-bold mb-1">총 등록 인원</span>
                                        <div className="flex items-end justify-between">
                                            <span className="text-2xl font-bold text-slate-900">{stats.studentCount}명</span>
                                            <span className={`text-xs font-bold ${stats.studentDelta >= 0 ? 'text-emerald-600' : 'text-rose-500'}`}>
                                                {stats.studentDelta >= 0 ? '+' : ''}{stats.studentDelta}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="p-4 flex flex-col">
                                        <span className="text-slate-500 text-xs font-bold mb-1">금주 출석률</span>
                                        <div className="flex items-end justify-between">
                                            <span className="text-2xl font-bold text-slate-900">{stats.attendanceRate}%</span>
                                            <span className={`text-xs font-bold ${stats.attendanceDelta > 0 ? 'text-emerald-600' : 'text-rose-500'}`}>
                                                {stats.attendanceDelta > 0 ? '+' : ''}{stats.attendanceDelta}%
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="divide-y divide-slate-200">
                                    <div className="p-4 flex flex-col">
                                        <span className="text-slate-500 text-xs font-bold mb-1">이번 달 심방</span>
                                        <div className="flex items-end justify-between">
                                            <span className="text-2xl font-bold text-slate-900">{stats.visitationCount}명</span>
                                            <span className="text-xs font-bold text-slate-400">목표 {stats.visitationRate}%</span>
                                        </div>
                                    </div>
                                    <div className="p-4 flex flex-col">
                                        <span className="text-slate-500 text-xs font-bold mb-1">재정 잔액</span>
                                        <div className="flex items-end justify-between">
                                            <span className="text-2xl font-bold text-slate-900">{stats.financeBalance.toLocaleString()}</span>
                                            <span className={`text-xs font-bold ${stats.financeDelta >= 0 ? 'text-emerald-600' : 'text-rose-500'}`}>
                                                {stats.financeDelta > 0 ? '+' : ''}{Math.round(stats.financeDelta / 10000)}만
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 2. Absentees */}
                        <div className="space-y-3">
                            <h2 className="text-lg font-bold text-slate-900">2. 장기 결석자 관리 및 면담</h2>
                            <table className="w-full text-sm border-collapse border border-slate-200">
                                <thead className="bg-slate-50 text-slate-700">
                                    <tr>
                                        <th className="p-2 border border-slate-200 font-bold">이름</th>
                                        <th className="p-2 border border-slate-200 font-bold">연락처</th>
                                        <th className="p-2 border border-slate-200 font-bold">상태</th>
                                        <th className="p-2 border border-slate-200 font-bold w-1/3">조치 및 면담 내용</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.absentees.map(s => (
                                        <tr key={s.id}>
                                            <td className="p-2 border border-slate-200 text-center">{s.name}</td>
                                            <td className="p-2 border border-slate-200 text-center text-slate-500">{s.phone}</td>
                                            <td className="p-2 border border-slate-200 text-center text-rose-600 font-bold">4주 결석</td>
                                            <td className="p-2 border border-slate-200"></td>
                                        </tr>
                                    ))}
                                    {stats.absentees.length === 0 && (
                                        <tr><td colSpan="4" className="p-6 text-center text-slate-400">장기 결석자가 없습니다.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* 3. Plans */}
                        <div className="space-y-3">
                            <div className="flex justify-between items-baseline">
                                <h2 className="text-lg font-bold text-slate-900 border-l-4 border-blue-500 pl-2">3. 금주 사역 결과 및 내주 계획</h2>
                                <span className="text-[10px] text-slate-400 font-medium">* 클릭하여 직접 입력 가능</span>
                            </div>
                            <textarea
                                className="w-full min-h-[140px] p-4 bg-slate-50 border border-slate-200 rounded-lg text-sm leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium"
                                placeholder="금주 진행된 사역 요약과 다음 주 진행될 핵심 일정을 입력하세요..."
                                value={reportPlans}
                                onChange={(e) => setReportPlans(e.target.value)}
                            />
                        </div>

                        {/* 4. Notes */}
                        <div className="space-y-3 mb-6">
                            <h2 className="text-lg font-bold text-slate-900 border-l-4 border-slate-400 pl-2">4. 비고 및 메모</h2>
                            <textarea
                                className="w-full min-h-[100px] p-4 bg-slate-50 border border-slate-200 rounded-lg text-sm leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-slate-400/20 focus:border-slate-400 transition-all font-medium text-slate-600"
                                placeholder="기타 특이사항, 부서 메모 등을 입력하세요..."
                                value={reportNotes}
                                onChange={(e) => setReportNotes(e.target.value)}
                            />
                        </div>


                        {/* Footer */}
                        <div className="mt-auto text-center text-xs text-slate-400 border-t border-slate-200 pt-6 font-medium">
                            TheSTAFF Ministry Management System
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default MinistryReportModal;
